import type { PropsWithChildren } from "react";

import type { LeadsSearchRow } from "app/pages/sales-intelligence/modules/leads-search";
import { getLeadDomainFromRow } from "app/pages/sales-intelligence/modules/leads-search/utils/rows";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import SignalsAdNetworksTooltip from "app/pages/sales-intelligence/sub-modules/signals/components/tooltips/SignalsAdNetworksTooltip/SignalsAdNetworksTooltip";
import type {
    COLUMN_AD_NETWORKS_ADDED_NAME,
    COLUMN_AD_NETWORKS_REMOVED_NAME,
} from "app/pages/sales-intelligence/sub-modules/signals/constants";
import { TRAFFIC_PERIOD_SHORT } from "app/pages/sales-intelligence/sub-modules/signals/constants/table-columns";
import type AdNetworkSignalSubType from "app/pages/sales-intelligence/sub-modules/signals/models/ad-network/AdNetworkSignalSubType";
import type AdNetworksSignalRowDto from "app/pages/sales-intelligence/sub-modules/signals/models/ad-network/AdNetworksSignalRowDto";
import TrafficChangesPeriod from "app/pages/sales-intelligence/sub-modules/signals/models/traffic/TrafficChangesPeriod";
import createSignalsTooltipViewModelFromArray from "app/pages/sales-intelligence/sub-modules/signals/models/view/signals-tooltip/common/factory";
import type SignalsTooltipViewModel from "app/pages/sales-intelligence/sub-modules/signals/models/view/signals-tooltip/SignalsTooltipViewModel";
import { is } from "app/pages/sales-intelligence/utils/type-guards";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

import createAdNetworksTooltipColumnsResolver from "../../../services/table-columns/ad-network/tooltipColumnsFactory";

interface ISignalAdNetworksTooltipContainerProps<V extends LeadsSearchRow> {
    field: typeof COLUMN_AD_NETWORKS_ADDED_NAME | typeof COLUMN_AD_NETWORKS_REMOVED_NAME;
    value: AdNetworksSignalRowDto[];
    row: V;
    signalSubType: AdNetworkSignalSubType;
    onViewAll: () => void;
}

export const SignalAdNetworksTooltipContainer = <V extends LeadsSearchRow>({
    children,
    row,
    value,
    field,
    signalSubType,
    onViewAll,
}: PropsWithChildren<ISignalAdNetworksTooltipContainerProps<V>>) => {
    if (is.undefined(value) || value.length === 0) {
        return children;
    }

    const redirectLink = siNavigator.getWebsiteAnalysisDisplayAdNetworksLink({
        key: getLeadDomainFromRow(row),
        isWWW: "*",
        country: WORLDWIDE_COUNTRY_ID,
        duration: TRAFFIC_PERIOD_SHORT[TrafficChangesPeriod.QuoterOverQuoter],
    });

    const createViewModel = (itemsLimit: number): SignalsTooltipViewModel => {
        const tableColumnsResolver = createAdNetworksTooltipColumnsResolver({
            parentColumnName: field,
            signalSubType,
        });

        return createSignalsTooltipViewModelFromArray<AdNetworksSignalRowDto>({
            tableColumnsResolver,
            values: value,
            itemsLimit,
        });
    };

    return (
        <SignalsAdNetworksTooltip
            signalSubType={signalSubType}
            redirectLink={redirectLink}
            createViewModel={createViewModel}
            onViewAll={onViewAll}
        >
            {children}
        </SignalsAdNetworksTooltip>
    );
};
